import Blockquote from '@tiptap/extension-blockquote';
import Bold from '@tiptap/extension-bold';
import Document from '@tiptap/extension-document';
import HardBreak from '@tiptap/extension-hard-break';
import Image from '@tiptap/extension-image';
import Italic from '@tiptap/extension-italic';
import Link from '@tiptap/extension-link';
import { BulletList, ListItem, OrderedList } from '@tiptap/extension-list';
import Paragraph from '@tiptap/extension-paragraph';
import Placeholder from '@tiptap/extension-placeholder';
import Text from '@tiptap/extension-text';
import { UndoRedo } from '@tiptap/extensions';
import { EditorContent, Extension, Extensions, useEditor } from '@tiptap/react';

import {
    useEffect,
    useRef,
    useState,
    type ChangeEvent,
    type MouseEvent,
    type RefObject,
} from 'react';

import { uploadPicture, uploadPublicDocument } from '~/admin/api';
import { useAuthData } from '~/auth';
import Button, { type ButtonIconName } from '~/components/new/Button';
import Form, { Input } from '~/components/ui/Form';
import { useOnClickOutside } from '~/components/ui/hooks';
import Modal, { useModal } from '~/components/ui/Modal';
import { useToasts } from '@remotely/ui-kit';
import { parseMarkdownWithHtml } from '~/utils/markdown';
import type { DataTestAttributes } from '~/utils/qa';
import { classNames } from '~/utils/styles';
import { RemixIconNames } from './Icon';

function isToolbarMarkActive(
    editor: NonNullable<ReturnType<typeof useEditor>>,
    mark: string,
): boolean {
    const { state } = editor;
    if (!state.selection.empty) {
        return editor.isActive(mark);
    }
    const markType = state.schema.marks[mark];
    if (!markType) {
        return false;
    }
    return state.selection.$from
        .marks()
        .some((m: { type: { name: string } }) => m.type.name === mark);
}

export function normalizeLinkUrl(url: string): string {
    const trimmed = url.trim();
    if (!trimmed) {
        return '';
    }
    if (/^[a-z][a-z0-9+.-]*:/i.test(trimmed)) {
        return trimmed;
    }
    return `https://${trimmed}`;
}

function applyLink(editor: NonNullable<ReturnType<typeof useEditor>>, url: string, text: string) {
    const trimmedUrl = url.trim();

    if (!trimmedUrl) {
        if (editor.isActive('link')) {
            editor.chain().focus().extendMarkRange('link').unsetLink().run();
        }
        return;
    }

    const href = normalizeLinkUrl(trimmedUrl);
    const { empty } = editor.state.selection;

    if (!empty) {
        editor.chain().focus().setLink({ href, target: '_blank' }).run();
        return;
    }

    const trimmedText = text.trim();
    if (trimmedText) {
        editor
            .chain()
            .focus()
            .insertContent({
                type: 'text',
                text: trimmedText,
                marks: [{ type: 'link', attrs: { href, target: '_blank' } }],
            })
            .run();
        return;
    }

    if (editor.isActive('link')) {
        editor.chain().focus().extendMarkRange('link').setLink({ href, target: '_blank' }).run();
        return;
    }

    editor.chain().focus().setLink({ href, target: '_blank' }).run();
}

const EXTENSIONS: Extensions = [
    Document,
    Paragraph,
    Text,
    HardBreak,
    Bold,
    Italic,
    BulletList,
    OrderedList,
    ListItem,
    Link.configure({
        openOnClick: false,
        autolink: false,
        defaultProtocol: 'https',
    }),
    Blockquote,
    Image,
    UndoRedo,
];

export type TextEditorProps = {
    value?: string;
    disabled?: boolean;
    onChange?: (value: string) => void;
    height?: number | string;
    withFileUploader?: boolean;
    variables?: Record<string, string>;
    placeholder?: string;
} & DataTestAttributes;

export default function TextEditor({
    value = '',
    disabled,
    onChange,
    height,
    withFileUploader,
    variables,
    placeholder,
    ['data-test-component']: testComponent = 'ui/TextEditor',
    ['data-test-name']: testName,
    ['data-test-id']: testId,
}: TextEditorProps) {
    const content = parseMarkdownWithHtml(value, true);
    return (
        <div
            className='text-editor'
            data-test-component={testComponent}
            data-test-name={testName}
            data-test-id={testId}>
            <RichTextEditor
                content={content}
                disabled={disabled}
                onChange={onChange}
                height={height}
                withFileUploader={withFileUploader}
                variables={variables}
                placeholder={placeholder}
            />
        </div>
    );
}

export type RichTextEditorProps = {
    content: string;
    disabled?: boolean;
    onChange?: (content: string) => void;
    height?: number | string;
    withFileUploader?: boolean;
    variables?: Record<string, string>;
    placeholder?: string;
};
function RichTextEditor({
    content,
    disabled = false,
    onChange,
    height,
    withFileUploader = false,
    variables,
    placeholder,
}: RichTextEditorProps) {
    const { user } = useAuthData();
    const [typingVariable, setTypingVariable] = useState(false);
    const [selectionRevision, setSelectionRevision] = useState(0);
    const skipNextContentSync = useRef(false);
    const extensions: Extensions = placeholder
        ? [...EXTENSIONS, Placeholder.configure({ placeholder })]
        : [...EXTENSIONS];
    if (variables) {
        const detectVariableExtension = Extension.create({
            onUpdate() {
                const anchor = this.editor.view.state.selection.$anchor;
                const text = anchor.nodeBefore?.text || '';
                if (!text || text.length < 2) return;

                if (text.endsWith('{{')) {
                    setTypingVariable(true);
                    this.editor.chain().blur().run();
                }
            },
        });
        extensions.push(detectVariableExtension);
    }

    const editor = useEditor({
        extensions,
        editable: !disabled && !typingVariable,
        content, // this will only have effect on the first render
        onUpdate: ({ editor }: { editor: ReturnType<typeof useEditor> }) => {
            setSelectionRevision((revision) => revision + 1);
            if (editor && onChange) {
                skipNextContentSync.current = true;
                onChange(String(editor.getHTML()));
            }
        },
        onSelectionUpdate: () => {
            setSelectionRevision((revision) => revision + 1);
        },
        editorProps: {
            attributes: {
                class: 'text-editor-input',
                style: `height: ${typeof height === 'number' ? `${height}rem` : height} !important`,
            },
        },
    });

    // content in the editor is not updated after the first render. To keep it
    // updated with outside changes to value we must keep track of changes
    // and update it manually.
    useEffect(() => {
        if (!editor) return;
        if (skipNextContentSync.current) {
            skipNextContentSync.current = false;
            return;
        }
        if (editor.getHTML() !== content) {
            editor.commands.setContent(content, { emitUpdate: false });
        }
    }, [content, editor]);

    return (
        <div
            className={classNames({ 'text-editor': true, disabled: disabled })}
            data-test-component='ui/TextEditor/RichTextEditor'>
            <ToolBar
                editor={editor}
                selectionRevision={selectionRevision}
                disabled={disabled}
                variables={variables}
                canUpload={withFileUploader && user.isTalent()}
                typingVariable={typingVariable}
                onAddVariable={() => setTypingVariable(false)}
            />
            <EditorContent
                editor={editor}
                data-test-component='ui/TextEditor/EditorContent'
            />
        </div>
    );
}

export type ToolBarProps = {
    editor: ReturnType<typeof useEditor>;
    selectionRevision: number;
    disabled: boolean;
    variables?: Record<string, string>;
    canUpload: boolean;
    typingVariable: boolean;
    onAddVariable: () => void;
} & DataTestAttributes;
function ToolBar({
    editor,
    selectionRevision,
    disabled,
    variables,
    canUpload,
    typingVariable,
    onAddVariable,
    ['data-test-component']: testComponent = 'ui/TextEditor/ToolBar',
    ['data-test-name']: testName,
    ['data-test-id']: testId,
}: ToolBarProps) {
    const linkModal = useModal<{ url: string; selection: boolean }>(false);
    const [showVariables, setShowVariables] = useState(false);
    const [loading, setLoading] = useState(disabled);
    const [file, setFile] = useState('');
    const uploadRef = useRef<HTMLInputElement>(null!);
    const toasts = useToasts();

    useEffect(() => {
        if (typingVariable) {
            setShowVariables(true);
        }
    }, [typingVariable]);

    if (!editor) return null;

    void selectionRevision;
    const isBold = isToolbarMarkActive(editor, 'bold');
    const isItalic = isToolbarMarkActive(editor, 'italic');
    const isBulletList = editor.isActive('bulletList');
    const isOrderedList = editor.isActive('orderedList');
    const isLink = isToolbarMarkActive(editor, 'link');

    const onFileUpload = (url: string, type: string, name: string) => {
        if (isImage(type)) {
            editor.chain().focus().setImage({ src: url, alt: name }).run();
        } else {
            editor
                .chain()
                .focus()
                .extendMarkRange('link')
                .setLink({ href: url, target: '_blank' })
                .command(({ tr }: { tr: Record<string, any> }) => {
                    tr.insertText(name);
                    return true;
                })
                .selectParentNode()
                .run();
        }
    };

    const handleUpload = (ref: RefObject<HTMLInputElement>) => {
        const files = ref.current.files!;
        if (files.length === 0) return;
        if (loading) return;

        const file = files[0];

        if (!allowedFileTypes.includes(file.type)) {
            toasts?.error(
                `File type ${file.type} not allowed; use one of the following: ${allowedFileTypes.join(', ')}.`,
            );
            return;
        }

        if (file.size > maxPictureSize) {
            toasts.error(
                `File is too big (${megabytes(file.size)}); it should be smaller than ${megabytes(maxPictureSize)}.`,
            );
            return;
        }

        const uploader = allowedImagesTypes.includes(file.type)
            ? uploadPicture
            : (file: File) => uploadPublicDocument('document', file);

        setLoading(true);

        uploader(file)
            .then((url: string) => {
                onFileUpload(url, file.type, file.name);
            })
            .catch((err) => {
                toasts.error(`File upload failed: ${[err].flat().join(', ')}.`);
            })
            .finally(() => {
                setLoading(false);
                setFile('');
            });
    };

    const setLink = (url: string, text: string) => {
        applyLink(editor, url, text);
    };

    const addVariable = (v: string) => {
        onAddVariable();
        setShowVariables(false);
        editor
            .chain()
            .focus()
            .command(({ tr }: { tr: Record<string, any> }) => {
                if (typingVariable) {
                    tr.insertText(` ${v} }}`);
                } else {
                    tr.insertText(`{{ ${v} }}`);
                }
                return true;
            })
            .run();
    };

    const cancelAddVariable = () => {
        setShowVariables(false);
        onAddVariable();
        editor.chain().focus().run();
    };

    return (
        <div
            className='text-editor-toolbar'
            data-test-component={testComponent}
            data-test-name={testName}
            data-test-id={testId}>
            <Action
                icon={RemixIconNames.RiBold}
                title='Bold'
                disabled={disabled}
                onClick={() => editor.chain().focus().toggleBold().run()}
                isActive={isBold}
            />
            <Action
                icon={RemixIconNames.RiItalic}
                title='Italic'
                disabled={disabled}
                onClick={() => editor.chain().focus().toggleItalic().run()}
                isActive={isItalic}
            />
            <Action
                icon={RemixIconNames.RiListUnordered}
                title='Bullet point list'
                disabled={disabled}
                onClick={() => editor.chain().focus().toggleBulletList().run()}
                isActive={isBulletList}
            />
            <Action
                icon={RemixIconNames.RiListOrdered2}
                title='Ordered list'
                disabled={disabled}
                onClick={() => editor.chain().focus().toggleOrderedList().run()}
                isActive={isOrderedList}
            />
            <Action
                icon={RemixIconNames.RiLinksLine}
                title='Add link'
                disabled={disabled}
                onClick={() => {
                    const attrs = editor.getAttributes('link');
                    linkModal.open({
                        url: attrs.href,
                        selection: !editor.view.state.selection.empty,
                    });
                }}
                isActive={isLink}
            />
            {variables && (
                <Action
                    icon={RemixIconNames.RiFlashlightLine}
                    title='Add variable'
                    disabled={disabled}
                    onClick={() => {
                        editor.chain().blur().run();
                        setShowVariables(true);
                    }}
                />
            )}
            {canUpload && (
                <Action
                    icon={RemixIconNames.RiAttachment2}
                    title='Attach file'
                    disabled={disabled}
                    onClick={() => uploadRef.current.click()}
                />
            )}
            {linkModal.isOpen && (
                <LinkModal
                    {...linkModal}
                    onAdd={setLink}
                />
            )}
            {showVariables && variables && (
                <VariablesMenu
                    variables={variables}
                    onSelect={addVariable}
                    disabled={disabled}
                    position={editor.view.coordsAtPos(editor.view.state.selection.$anchor.pos)}
                    onClose={cancelAddVariable}
                />
            )}
            {canUpload && (
                <input
                    value={file}
                    className='file-upload-hidden'
                    disabled={disabled}
                    type='file'
                    accept={allowedFileTypes.join(',')}
                    ref={uploadRef}
                    onChange={() => handleUpload(uploadRef)}
                />
            )}
        </div>
    );
}

export type ActionProps = {
    icon: ButtonIconName;
    title: string;
    disabled: boolean;
    onClick: () => void;
    isActive?: boolean;
};

function Action({ icon, title, disabled, onClick, isActive = false }: ActionProps) {
    return (
        <Button
            icon={icon}
            variant={isActive ? 'filled-light' : 'floating'}
            disabled={disabled}
            onClick={onClick}
            title={title}
            data-testid={title}
            size='medium'
        />
    );
}

export type LinkModalProps = {
    close: () => void;
    url: string;
    onAdd: (url: string, text: string) => void;
    selection: boolean;
};

function LinkModal({ close, url: initialUrl, onAdd, selection }: LinkModalProps) {
    const [text, setText] = useState('');
    const [url, setUrl] = useState(initialUrl || '');
    const title = !initialUrl ? 'Add link' : 'Edit link';
    return (
        <Modal
            usePortal
            onClose={close}
            size='small'>
            <Modal.Header>{title}</Modal.Header>
            <Modal.Body>
                <div className='form-section'>
                    {!initialUrl && !selection && (
                        <Form.Field>
                            <Form.Label>Text</Form.Label>
                            <Input
                                placeholder='Enter text'
                                value={text}
                                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                    setText(e.target.value)
                                }
                            />
                        </Form.Field>
                    )}
                    <Form.Field>
                        <Form.Label>URL</Form.Label>
                        <Input
                            placeholder='Enter URL'
                            value={url}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => setUrl(e.target.value)}
                        />
                    </Form.Field>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button
                    data-action='cancel'
                    variant='floating'
                    onClick={close}>
                    Cancel
                </Button>
                <Button
                    onClick={(e: MouseEvent<HTMLButtonElement>) => {
                        e.stopPropagation();
                        e.preventDefault();
                        onAdd(url, text);
                        close();
                    }}>
                    {title}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

export type VariablesMenuProps = {
    variables: Record<string, string>;
    disabled: boolean;
    onSelect: (variable: string) => void;
    onClose: () => void;
    position: { left: number; top: number };
};

function VariablesMenu({ variables, disabled, onSelect, onClose, position }: VariablesMenuProps) {
    const ref = useRef<HTMLUListElement>(null);
    const [selected, setSelected] = useState(0);
    const keys = Object.keys(variables);
    useOnClickOutside(ref as RefObject<HTMLElement>, onClose);

    useEffect(() => {
        if (ref.current) {
            const windowHeight = window.innerHeight;
            const windowWidth = window.innerWidth;
            const { width, height } = ref.current.getBoundingClientRect();
            const { left, top } = position;

            if (left + width > windowWidth && left - width > 0) {
                ref.current.style.left = `${left - width}px`;
            } else {
                ref.current.style.left = `${left}px`;
            }

            if (top + height > windowHeight && top - height > 0) {
                ref.current.style.top = `${top - height}px`;
            } else {
                ref.current.style.top = `${top}px`;
            }
        }
    }, [position]);

    useEffect(() => {
        const handleSelect = (e: KeyboardEvent) => {
            e.preventDefault();
            e.stopPropagation();

            switch (e.keyCode) {
                case 38: // up
                    setSelected(selected - 1 < 0 ? keys.length - 1 : selected - 1);
                    break;
                case 40: // down
                    setSelected((selected + 1) % keys.length);
                    break;
                case 13: // enter
                    onSelect(keys[selected]);
            }
        };

        window.addEventListener('keydown', handleSelect);
        return () => {
            window.removeEventListener('keydown', handleSelect);
        };
    }, [selected]);

    return (
        <div className='text-editor-variables-overlay'>
            <ul
                className='text-editor-variables'
                ref={ref}>
                {Object.keys(variables).map((key, i) => (
                    <li
                        key={key}
                        className={classNames({
                            selected: i === selected,
                        })}
                        onClick={() => {
                            if (disabled) return;
                            onSelect(key);
                        }}>
                        <span className='text-editor-variables-label'>{variables[key]}</span>
                        <span className='text-editor-variables-var'>{`{{ ${key} }}`}</span>
                    </li>
                ))}
            </ul>
        </div>
    );
}

const maxPictureSize = 5 * 1024 * 1024;

const allowedImagesTypes = ['image/jpeg', 'image/jpg', 'image/png'];
const allowedDocumentsTypes = ['application/pdf', 'application/zip', 'application/vnd.rar'];
const allowedFileTypes = allowedImagesTypes.concat(allowedDocumentsTypes);

export function isImage(type: string) {
    return allowedImagesTypes.includes(type);
}

function megabytes(bytes: number) {
    return `${Math.round((10 * bytes) / 1024 / 1024) / 10} MB`;
}

export function isEmptyText(text: string | null | undefined): text is '' | null | undefined {
    return !text || !text.trim() || text === '<p></p>';
}

export function htmlToPlainText(html: string): string {
    if (typeof document === 'undefined') {
        return html.replace(/<[^>]*>/g, '').trim();
    }
    return (new DOMParser().parseFromString(html, 'text/html').body.textContent ?? '').trim();
}
